import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, Users, Zap, Mic, Camera, ArrowRight, Wrench, BookOpen, GraduationCap, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { uploadImage, useAuthedImage } from "@/lib/files";
import { ImpactStrip } from "@/components/ImpactStrip";

export default function Home() {
  const [text, setText] = useState("");
  const [askMode, setAskMode] = useState("both");
  const [aiModel, setAiModel] = useState("auto");
  const [models, setModels] = useState([{ id: "auto", name: "AUTO — Let CLING choose" }]);
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(false);
  const [photoPath, setPhotoPath] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const nav = useNavigate();
  const { user } = useAuth();
  const photoSrc = useAuthedImage(photoPath);

  useEffect(() => {
    api.get("/ai/models").then(({ data }) => setModels(data));
    api.get("/help-requests?scope=mine").then(({ data }) => setRecent(data));
  }, []);

  const onPickPhoto = () => fileRef.current?.click();
  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const { path } = await uploadImage(f);
      setPhotoPath(path);
      toast.success("Photo attached");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const create = async () => {
    if (!text.trim()) return toast.error("Describe what you need help with");
    setBusy(true);
    try {
      const { data } = await api.post("/help-requests", {
        text,
        attachments: photoPath ? [photoPath] : [],
        ask_mode: askMode,
        ai_model: aiModel,
      });
      nav(`/match/${data.request_id}`);
    } catch {
      toast.error("Could not create help request");
    } finally {
      setBusy(false);
    }
  };

  const quickActions = [
    { i: Wrench, label: "Fix something", prompt: "I'm trying to fix " },
    { i: BookOpen, label: "Learn something", prompt: "I want to learn " },
    { i: GraduationCap, label: "Find a teacher", prompt: "Looking for a teacher for " },
    { i: Users, label: "Find my people", prompt: "I want to connect with people who know about " },
  ];

  return (
    <div className="min-h-screen warm-grain">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-16">
        <div className="rise-in">
          <div className="text-xs font-mono-label text-[color:hsl(var(--teal))]">Hi {user?.name?.split(" ")[0]} — what's on your mind?</div>
          <h1 className="mt-2 font-display font-extrabold text-3xl sm:text-5xl leading-tight">
            What do you need help with<span className="text-[color:hsl(var(--amber))]">?</span>
          </h1>
        </div>

        <div className="mt-6 border rounded-3xl bg-card p-5 shadow-sm rise-in-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. Replacing the radiator on my 2018 Nissan Titan — stuck removing the lower hose."
            rows={4}
            className="border-0 shadow-none text-base resize-none focus-visible:ring-0 px-0"
            data-testid="home-help-input"
          />
          {photoPath && photoSrc && (
            <div className="mt-3 relative inline-block" data-testid="home-photo-preview">
              <img src={photoSrc} alt="attached" className="rounded-xl border max-h-48" />
              <button
                onClick={() => setPhotoPath(null)}
                className="absolute -top-2 -right-2 bg-background border rounded-full p-1 shadow"
                data-testid="home-photo-remove"
                aria-label="Remove photo"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-3 items-center border-t pt-3">
            <button className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground" data-testid="home-mic-btn" onClick={() => toast.info("Voice input coming soon")}>
              <Mic className="w-4 h-4" /> Voice
            </button>
            <button
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
              data-testid="home-photo-btn"
              onClick={onPickPhoto}
              disabled={uploading}
            >
              <Camera className="w-4 h-4" /> {uploading ? "Uploading…" : photoPath ? "Change photo" : "Photo"}
            </button>
            <input ref={fileRef} onChange={onPhoto} type="file" accept="image/*" className="hidden" data-testid="home-photo-input" />
            <div className="flex-1" />
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono-label text-muted-foreground">ASK</span>
              <div className="inline-flex rounded-full border p-0.5 bg-secondary/50">
                {[
                  { k: "people", label: "People", i: Users },
                  { k: "ai", label: "AI", i: Sparkles },
                  { k: "both", label: "Both", i: Zap },
                ].map(({ k, label, i: I }) => (
                  <button
                    key={k}
                    onClick={() => setAskMode(k)}
                    data-testid={`ask-mode-${k}`}
                    className={`px-3 py-1 rounded-full text-xs inline-flex items-center gap-1 transition ${askMode === k ? "bg-[color:hsl(var(--teal))] text-white" : "text-muted-foreground"}`}
                  >
                    <I className="w-3 h-3" /> {label}
                  </button>
                ))}
              </div>
            </div>
            <Select value={aiModel} onValueChange={setAiModel}>
              <SelectTrigger className="h-8 w-[180px] text-xs" data-testid="ai-model-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {models.map((m) => (
                  <SelectItem key={m.id} value={m.id} className="text-xs">{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={create} disabled={busy} className="btn-teal rounded-full h-9 px-4" data-testid="home-submit-help">
              {busy ? "Matching…" : <>Get help <ArrowRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 rise-in-3">
          {quickActions.map(({ i: I, label, prompt }) => (
            <button
              key={label}
              onClick={() => setText(prompt)}
              data-testid={`quick-${label.toLowerCase().replace(/\s/g, "-")}`}
              className="border rounded-2xl bg-card px-4 py-4 text-left hover:border-[color:hsl(var(--teal))] transition"
            >
              <I className="w-4 h-4 text-[color:hsl(var(--teal))]" />
              <div className="mt-2 text-sm font-medium">{label}</div>
            </button>
          ))}
        </div>

        <ImpactStrip variant="home" />

        <div className="mt-12">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-xl">Your recent help requests</h2>
          </div>
          {recent.length === 0 ? (
            <div className="border rounded-2xl bg-card p-6 text-sm text-muted-foreground" data-testid="empty-recent">
              You haven't asked anything yet. When you do, it will show up here.
            </div>
          ) : (
            <div className="grid gap-3">
              {recent.map((r) => (
                <button
                  key={r.request_id}
                  onClick={() => nav(`/match/${r.request_id}`)}
                  data-testid={`recent-${r.request_id}`}
                  className="text-left border rounded-2xl bg-card p-4 hover:border-[color:hsl(var(--teal))] transition"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xs font-mono-label text-[color:hsl(var(--teal))]">{r.category}</div>
                      <div className="mt-1 font-medium">{r.text}</div>
                    </div>
                    <span className={`text-xs font-mono-label rounded-full border px-2 py-0.5 ${r.status === "solved" ? "text-emerald-600 border-emerald-500/40" : "text-muted-foreground"}`}>{r.status}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
